import { ApiConstants } from '@epi-constants/actions';
import { OrderHistorySorting } from '@epi-constants/orderHistorySorting';
import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';

const loginSuccess = createAction(`${ApiConstants.LOGIN_ACTION}_SUCCESS`);

export type OrderHistoryQueryParameters = {
  page: number;
  rowsPerPage: number;
  sortingBy: string;
  descending: boolean;
};

export type OrderHistoryState = {
  noPersistance: boolean;
  queryParameters: OrderHistoryQueryParameters;
};

export const getInitialState = (): OrderHistoryState => ({
  noPersistance: true,
  queryParameters: {
    page: 1,
    rowsPerPage: 5,
    sortingBy: OrderHistorySorting.ByDate,
    descending: true
  }
});

const initialState = getInitialState();

const orderHistorySlice = createSlice({
  name: 'orderHistory',
  initialState,
  reducers: {
    resetQueryParameters: state => {
      state.queryParameters = { ...getInitialState().queryParameters };
    },
    updateQueryParameters: (
      state,
      { payload }: PayloadAction<OrderHistoryQueryParameters>
    ) => {
      state.queryParameters = payload;
    }
  },
  extraReducers: builder => {
    builder.addCase(loginSuccess, state => {
      state.queryParameters = { ...getInitialState().queryParameters };
    });
  }
});

export const { reducer, actions: orderHistoryActions } = orderHistorySlice;

type PartialQueryParameters = Partial<OrderHistoryQueryParameters>;

const buildQueryParameters = (
  current: OrderHistoryQueryParameters,
  queryParameters?: PartialQueryParameters
): OrderHistoryQueryParameters => {
  let descending = current.descending;

  if (queryParameters?.sortingBy && queryParameters.descending === undefined) {
    descending =
      queryParameters.sortingBy === current.sortingBy
        ? !current.descending
        : true;
  }

  return {
    page: queryParameters?.page ?? current.page,
    rowsPerPage: queryParameters?.rowsPerPage ?? current.rowsPerPage,
    sortingBy:
      queryParameters?.sortingBy ??
      current.sortingBy ??
      OrderHistorySorting.ByDate,
    descending
  };
};

export const loadVoucherOrdersHistory =
  (queryParameters?: PartialQueryParameters) => (dispatch, getState) => {
    const current = getState().orderHistory.queryParameters;
    const newQueryParameters = buildQueryParameters(current, queryParameters);

    dispatch(orderHistoryActions.updateQueryParameters(newQueryParameters));
    dispatch({ type: `${ApiConstants.LOAD_VOUCHER_ORDERS_HISTORY}_REQUEST` });
  };

export const resetOrderHistoryParameters = () =>
  orderHistoryActions.resetQueryParameters();
